<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="queueName"
        :header-content="$t('QUEUES_MGMT.AGENT.BROWSE_MODAL.SUB_TEXT')"
      />
      <div class="p-8 pt-4">
        <woot-loading-state
          v-if="isLoading"
          :message="$t('QUEUES_MGMT.AGENT.BROWSE_MODAL.LOADING')"
        />
        <p v-else-if="!conversations.length" class="text-slate-500">
          {{ $t('QUEUES_MGMT.AGENT.BROWSE_MODAL.EMPTY') }}
        </p>
        <table v-else class="woot-table">
          <thead>
            <th
              v-for="header in $t(
                'QUEUES_MGMT.AGENT.BROWSE_MODAL.TABLE_HEADER'
              )"
              :key="header"
            >
              {{ header }}
            </th>
          </thead>
          <tbody>
            <tr v-for="row in conversations" :key="row.conversation_id">
              <td>#{{ row.conversation_id }}</td>
              <td>{{ row.contact_name || '—' }}</td>
              <td>{{ expansionLabel(row.expansion_trigger) }}</td>
              <td>
                <span v-if="isExcluded(row)" class="text-xs text-yellow-700">
                  {{ $t('QUEUES_MGMT.AGENT.BROWSE_MODAL.EXCLUDED') }}
                </span>
              </td>
              <td>
                <woot-button
                  size="tiny"
                  color-scheme="primary"
                  :is-loading="takingId === row.conversation_id"
                  @click="onTake(row)"
                >
                  {{ $t('QUEUES_MGMT.AGENT.BROWSE_MODAL.TAKE') }}
                </woot-button>
              </td>
            </tr>
          </tbody>
        </table>
        <queue-pager :meta="meta" @page="load" />
      </div>
    </div>

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('QUEUES_MGMT.AGENT.CONFIRM.TITLE')"
      :description="confirmMessage"
      :confirm-label="$t('QUEUES_MGMT.AGENT.CONFIRM.PROCEED')"
      :cancel-label="$t('QUEUES_MGMT.AGENT.CONFIRM.CANCEL')"
    />
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import QueueActionsAPI from 'dashboard/api/queueActions';
import QueuePager from './QueuePager.vue';

const EMPTY_META = { current_page: 1, total_pages: 0, total_count: 0 };

export default {
  components: { QueuePager },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    queueId: {
      type: Number,
      required: true,
    },
    queueName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      conversations: [],
      isLoading: false,
      takingId: null,
      confirmMessage: '',
      meta: EMPTY_META,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      // QM §5.4.3 — availability lives on the per-account record, not on the root
      // user object; `currentUser.availability_status` is always undefined.
      currentUserAvailability: 'getCurrentUserAvailability',
    }),
    // QM §5.4.3, §4.6 — same source the §5.4.1 capacity indicator uses.
    isAtCapacity() {
      const { open_count: open, cap } = this.currentUser?.queue_capacity || {};
      return cap != null && open != null && open >= cap;
    },
  },
  watch: {
    show(isVisible) {
      if (isVisible) this.load();
    },
  },
  mounted() {
    if (this.show) this.load();
  },
  methods: {
    async load(page = 1) {
      this.isLoading = true;
      try {
        const { payload, meta } = await this.$store.dispatch(
          'queues/getConversations',
          { queueId: this.queueId, page }
        );
        this.conversations = payload;
        this.meta = meta;
      } catch (error) {
        this.showAlert(this.$t('QUEUES_MGMT.AGENT.TOAST.ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    isExcluded(row) {
      return row.excluded_agent_id === this.currentUser.id;
    },
    expansionLabel(trigger) {
      const key = (trigger || 'none').toUpperCase();
      return this.$t(`QUEUES_MGMT.AGENT.BROWSE_MODAL.EXPANSION.${key}`);
    },
    // QM §5.4.3 — every override condition that applies is listed in one dialog.
    overrideReasons(row) {
      const reasons = [];
      if (this.isAtCapacity) {
        reasons.push(this.$t('QUEUES_MGMT.AGENT.CONFIRM.AT_CAPACITY'));
      }
      const status = this.currentUserAvailability;
      if (status === 'busy') {
        reasons.push(this.$t('QUEUES_MGMT.AGENT.CONFIRM.BUSY'));
      } else if (status === 'offline') {
        reasons.push(this.$t('QUEUES_MGMT.AGENT.CONFIRM.OFFLINE'));
      }
      if (this.isExcluded(row)) {
        reasons.push(this.$t('QUEUES_MGMT.AGENT.CONFIRM.EXCLUDED'));
      }
      return reasons;
    },
    async onTake(row) {
      const reasons = this.overrideReasons(row);
      if (reasons.length) {
        this.confirmMessage = reasons.join(' ');
        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (!ok) return;
      }
      this.take(row);
    },
    async take(row) {
      this.takingId = row.conversation_id;
      try {
        await QueueActionsAPI.take(row.conversation_id);
        this.showAlert(
          this.$t('QUEUES_MGMT.AGENT.TOAST.TAKEN', {
            id: row.conversation_id,
          })
        );
        this.conversations = this.conversations.filter(
          c => c.conversation_id !== row.conversation_id
        );
        this.$emit('taken', row.conversation_id);
      } catch (error) {
        const status = error?.response?.status;
        if (status === 409) {
          this.showAlert(this.$t('QUEUES_MGMT.AGENT.TOAST.ALREADY_TAKEN'));
          this.conversations = this.conversations.filter(
            c => c.conversation_id !== row.conversation_id
          );
          this.$emit('taken', row.conversation_id);
        } else if (status === 403) {
          this.showAlert(this.$t('QUEUES_MGMT.AGENT.TOAST.FORBIDDEN'));
        } else {
          this.showAlert(this.$t('QUEUES_MGMT.AGENT.TOAST.ERROR'));
        }
      } finally {
        this.takingId = null;
      }
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>
